import React, {
  ChangeEvent,
  FocusEventHandler,
  InputHTMLAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  RefObject,
} from 'react';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import isNil from 'lodash/isNil';
import type {
  InputValue,
} from 'core/components/formValue';

type Part = 'root' | 'container' | 'input' | 'label';

type Props = {
  className?: string;
  classes?: Partial<Record<Part, string>>;
  labelClasses?: string;
  inputClasses?: string;
  readOnlyValueClassName?: string;
  type?: string;
  id?: string;
  label?: string | ReactNode;
  value?: InputValue;
  defaultValue?: InputValue;
  placeholder?: string;
  onChange?: (val: string, event?: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClick?: MouseEventHandler<HTMLElement>;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  required?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  allowSpeechToText?: boolean;
  disabled?: boolean;
  nonEditable?: boolean;
  icon?: ReactNode;
  endIcon?: ReactNode;
  inputRef?: RefObject<HTMLInputElement | null>;
  sx?: object;
  error?: boolean;
  showErrorText?: boolean;
  trackingLabel?: string;
};

export default function Input({
  className,
  labelClasses,
  inputClasses,
  readOnlyValueClassName,
  id = '',
  label,
  value,
  placeholder,
  onChange,
  required,
  allowSpeechToText,
  disabled,
  nonEditable,
  icon,
  endIcon,
  type,
  sx,
  inputProps,
  inputRef,
  onBlur,
  onClick,
  onKeyDown,
  onFocus,
  defaultValue,
  error,
  showErrorText = true,
  trackingLabel,
  classes,
}: Props) {
  const textInputClasses = clsx(
    'populate-input',
    { 'no-border': error },
    inputClasses,
    classes?.input,
  );
  const resolvedValue = isNil(value)
    ? (defaultValue ?? '')
    : value;

  return (
    <Wrapper
      id={id}
      label={label}
      className={clsx(className, classes?.root)}
      micClassName="!absolute"
      labelClasses={clsx(labelClasses, classes?.label)}
      containerClassName={clsx({ 'populate-field-shell h-full items-center px-4': nonEditable }, classes?.container)}
      required={required}
      error={error}
      showErrorText={showErrorText}
      allowSpeechToText={allowSpeechToText && !nonEditable && !disabled}
      onChange={onChange}
      trackingLabel={trackingLabel}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      {nonEditable ? (
        <p className={clsx('text-xs', readOnlyValueClassName)}>
          {value as ReactNode}
        </p>
      ) : (
        <TextField
          type={type}
          inputRef={inputRef}
          className={textInputClasses}
          id={id}
          sx={sx}
          value={resolvedValue as string | number | readonly string[]}
          placeholder={placeholder}
          onChange={(e: ChangeEvent<HTMLInputElement>) => { onChange?.(e.target.value, e); }}
          tabIndex={-1}
          slotProps={{
            input: {
              startAdornment: icon,
              endAdornment: endIcon,
            },
            htmlInput: inputProps,
          }}
          disabled={disabled}
          onBlur={onBlur}
          onClick={onClick}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
        />
      )}
    </Wrapper>
  );
}
